import React, { useState, useRef } from 'react';
import { 
  User, Mail, Phone, Lock, MapPin, 
  ArrowRight, Check, AlertCircle, Eye, EyeOff
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes.constants';
import PhoneInput from '../common/PhoneInput';
import Logo from '../common/Logo';
import OTPVerification from '../auth/OTPVerification';
import { extractCountryCode } from '../../utils/phoneUtils';

const UserRegistration = () => {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '+212',
    address: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const isSubmitting = useRef(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const phoneValue = e.target.value;
    const { countryCode, phoneNumber } = extractCountryCode(phoneValue, formData.countryCode);
    
    setFormData(prev => ({
      ...prev,
      countryCode,
      phoneNumber: phoneValue // Garder la valeur complète pour PhoneInput
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('userRegistration.firstNameRequired');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('userRegistration.lastNameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('userRegistration.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('userRegistration.emailInvalid');
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t('userRegistration.phoneRequired');
    }
    if (!formData.password) {
      newErrors.password = t('userRegistration.passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('userRegistration.passwordMinLength');
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('userRegistration.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('userRegistration.passwordMismatch');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Protection contre les doubles soumissions
    if (isSubmitting.current) return;
    
    if (!validateForm()) return;
    
    isSubmitting.current = true;
    setIsLoading(true);
    setErrors({});
    
    try {
      // Extract phone number parts
      const phoneMatch = formData.phoneNumber.match(/^(\+\d{1,4})(.+)$/);
      const countryCode = phoneMatch ? phoneMatch[1] : formData.countryCode || '+212';
      const phoneNumberOnly = phoneMatch ? phoneMatch[2].trim() : formData.phoneNumber.replace(/^\+\d{1,4}/, '').trim();
      
      const result = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: phoneNumberOnly,
        countryCode: countryCode,
        password: formData.password,
        role: 'user',
        userDetails: {
          driverLicense: 'PENDING', // À compléter plus tard
        },
      });

      if (result.success) {
        // Afficher la page de vérification OTP
        setRegisteredUser({
          id: result.data.user.id || result.data.user._id,
          phoneNumber: phoneNumberOnly,
          countryCode: countryCode,
        });
        setShowOTPVerification(true);
      } else {
        // Handle specific backend validation errors
        if (result.errors) {
          setErrors(prev => ({ ...prev, ...result.errors }));
        }
        setErrors(prev => ({
          ...prev,
          submit: result.message || t('userRegistration.submitError')
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error.message || t('userRegistration.submitError')
      }));
    } finally {
      setIsLoading(false);
      isSubmitting.current = false;
    }
  };

  // Gérer la vérification OTP réussie
  const handleOTPVerified = () => {
    setIsSubmitted(true);
    setTimeout(() => {
      navigate(ROUTES.HOME);
    }, 2000);
  };

  // Retourner à l'inscription depuis OTP
  const handleBackToSignUp = () => {
    setShowOTPVerification(false);
    setRegisteredUser(null);
    setErrors({});
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#27489E] via-[#1e3a7d] to-[#27489E] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('userRegistration.successTitle')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('userRegistration.successMessage')}
          </p>
          <button
            onClick={() => navigate(ROUTES.HOME)}
            className="w-full bg-[#27489E] text-white py-3 rounded-xl font-semibold hover:bg-[#1e3a7d] transition"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="text-4xl" carColor="text-[#27489E]" vyColor="text-[#F5A623]" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t('userRegistration.title')}
          </h1>
          <p className="text-gray-600">
            {t('userRegistration.subtitle')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          {/* Afficher la vérification OTP si nécessaire */}
          {showOTPVerification && registeredUser ? (
            <OTPVerification
              userId={registeredUser.id}
              phoneNumber={registeredUser.phoneNumber}
              countryCode={registeredUser.countryCode}
              onVerified={handleOTPVerified}
              onClose={handleBackToSignUp}
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{errors.submit}</span>
              </div>
            )}

            {/* First Name & Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('userRegistration.firstName')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border ${
                      errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                    placeholder={t('userRegistration.firstNamePlaceholder')}
                  />
                </div>
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('userRegistration.lastName')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border ${
                      errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                    placeholder={t('userRegistration.lastNamePlaceholder')}
                  />
                </div>
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userRegistration.email')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-11 pr-4 py-3 rounded-xl border ${
                    errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                  placeholder={t('userRegistration.emailPlaceholder')}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userRegistration.phone')} <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                error={errors.phoneNumber}
                placeholder="6 12 34 56 78"
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Address (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userRegistration.address')}
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full pl-11 pr-4 py-3 rounded-xl border resize-none ${
                    errors.address ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                  placeholder={t('userRegistration.addressPlaceholder')}
                />
              </div>
              {errors.address && (
                <p className="text-red-500 text-xs mt-1">{errors.address}</p>
              )}
            </div>

            {/* Password & Confirm Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('userRegistration.password')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-12 py-3 rounded-xl border ${
                      errors.password ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                    placeholder={t('userRegistration.passwordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('userRegistration.confirmPassword')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-12 py-3 rounded-xl border ${
                      errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                    placeholder={t('userRegistration.confirmPasswordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* Terms Checkbox */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      e.stopPropagation();
                      setAcceptedTerms(e.target.checked);
                    }}
                    required
                    className="sr-only"
                  />
                  <div 
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition cursor-pointer ${
                      acceptedTerms 
                        ? 'bg-[#27489E] border-[#27489E]' 
                        : 'border-gray-300 bg-white hover:border-[#27489E]'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setAcceptedTerms(!acceptedTerms);
                    }}
                  >
                    {acceptedTerms && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span 
                  className="text-sm text-gray-600 select-none"
                  onClick={(e) => {
                    e.preventDefault();
                    setAcceptedTerms(!acceptedTerms);
                  }}
                >
                  {t('userRegistration.termsText')}
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !acceptedTerms}
              className="w-full bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#27489E]/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  {t('userRegistration.submitButton')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserRegistration;

